// Angular
import { Injectable, OnDestroy } from "@angular/core";
import { BehaviorSubject, Subscription } from "rxjs";

// SF
import { SplitterComponent } from "@syncfusion/ej2-angular-layouts";


// Cerium
import { UiActionTypeEnum, UiCommand, UiCommandEvent, UiKeyboardShortcut } from "@ui/cer-ui-types";
import { CerFormService } from "../cer-form.service";
import { CerFormPaneView } from "./cer-form-pane/cer-form-pane-view";
import { CerUiIcons } from "@ui/cer-ui-icons";

export const uiCmdPaneRightToggle: UiCommand =
   { text: 'Vis detaljer', tooltipText: 'Vis detalje panel', id: 'paneRightToggle', align: 'right', iconCss: CerUiIcons.viewSide, toolbarShowTextOn: "Both", toolbarPosition: 'last', toolbarOverflow: 'show' };
export const uiCmdPaneRightToggleNoText: UiCommand =
   { text: '', tooltipText: 'Vis detalje panel', id: 'paneRightToggle', align: 'right', iconCss: CerUiIcons.viewSide, toolbarShowTextOn: "Overflow", toolbarPosition: 'last', toolbarOverflow: 'show' };
export const uiCmdPaneMainToggle: UiCommand =
   { text: 'Vis midterste', tooltipText: 'Vis miderste panel', id: 'paneMainToggle', align: 'right', iconCss: CerUiIcons.viewSideUp, toolbarShowTextOn: "Overflow", toolbarPosition: 'last', toolbarOverflow: 'show' };
export const uiCmdPaneBottomLeftToggle: UiCommand =
   { text: 'Vis nederste', tooltipText: 'Vis nederste panel', id: 'paneBottomLeftMainToggle', align: 'right', iconCss: CerUiIcons.viewSideUp, toolbarShowTextOn: "Overflow", toolbarPosition: 'last', toolbarOverflow: 'show' };
export const uiCmdPaneBottomToggle: UiCommand =
   { text: 'Vis nederste', tooltipText: 'Vis nederste panel', id: 'paneBottomToggle', align: 'right', iconCss: CerUiIcons.viewSideUp, toolbarShowTextOn: "Overflow", toolbarPosition: 'last', toolbarOverflow: 'show' };
export const uiCmdPaneBottomRightToggle: UiCommand =
   { text: 'Vis nederste', tooltipText: 'Vis nederste panel', id: 'paneBottomRightToggle', align: 'right', iconCss: CerUiIcons.viewSideUp, toolbarShowTextOn: "Overflow", toolbarPosition: 'last', toolbarOverflow: 'show' };
export const uiCmdPaneBottomRightToggleNoText: UiCommand =
   { text: '', tooltipText: 'Vis nederste', id: 'paneBottomToggle', align: 'right', iconCss: CerUiIcons.viewSideUp, toolbarShowTextOn: "Overflow", toolbarPosition: 'last', toolbarOverflow: 'show' };
export const uiCmdPaneRightShow: UiCommand =
   { text: '', tooltipText: 'Vis detalje panel', id: 'detailShow', align: 'left', iconCss: CerUiIcons.open };
export const uiCmdPaneRightClose: UiCommand =
   { text: '', tooltipText: 'Luk detalje panel', id: 'paneRightClose', align: 'left', iconCss: CerUiIcons.close };
export const uiCmdPaneBottomShow: UiCommand =
   { text: '', tooltipText: 'Vis nederste panel', id: 'paneBottomShow', align: 'left', iconCss: CerUiIcons.open };
export const uiCmdPaneBottomLeftShow: UiCommand =
   { text: '', tooltipText: 'Vis nederste panel', id: 'paneBottomLefthow', align: 'left', iconCss: CerUiIcons.open };
export const uiCmdPaneBottomRightShow: UiCommand =
   { text: '', tooltipText: 'Vis nederste panel', id: 'paneBottomRightShow', align: 'left', iconCss: CerUiIcons.open };
export const uiCmdPaneBottomLeftClose: UiCommand =
   { text: '', tooltipText: 'Luk nederste panel', id: 'paneBottomLeftClose', align: 'left', iconCss: CerUiIcons.close };
export const uiCmdPaneBottomClose: UiCommand =
   { text: '', tooltipText: 'Luk nederste panel', id: 'paneBottomClose', align: 'left', iconCss: CerUiIcons.close };
export const uiCmdPaneBottomRightClose: UiCommand =
   { text: '', tooltipText: 'Luk panel', id: 'paneBottomRightClose', align: 'left', iconCss: CerUiIcons.close };
export const uiCmdPaneMainClose: UiCommand =
   { text: '', tooltipText: 'Luk miderste panel', id: 'paneMainClose', align: 'left', iconCss: CerUiIcons.close };

export const uiCmdPaneRightReduce: UiCommand = { id: 'uiCmdPaneRightReduce', text: 'Reducer detaljer' };
export const uiCmdPaneRightEnlarge: UiCommand = { id: 'uiCmdPaneRightEnlarge', text: 'Udvid detaljer' };

export const uiShortcutPaneRightReduce: UiKeyboardShortcut = { cmd: uiCmdPaneRightReduce, code: 'PageUp', alt: true, ctrl: false, shift: true, enabled: true }; // Alt+Shift+PgUp
export const uiShortcutPaneRightEnlarge: UiKeyboardShortcut = { cmd: uiCmdPaneRightEnlarge, code: 'PageDown', alt: true, ctrl: false, shift: true, enabled: true }; // Alt+Shift+PgDn

const formPaneNames = ['top', 'main', 'left', 'right', 'info', 'bottomLeft', 'bottom', 'bottomRight'] as const;
export type CerFormPaneType = 'top' | 'main' | 'left' | 'right' | 'info' | 'bottomLeft' | 'bottom' | 'bottomRight';
export type CerFormPane = typeof formPaneNames[number];
export type CerFormPaneVisible = 'show' | 'collapse' | 'hide';
export type CerFormPanesOrientation = 'horizontal' | 'vertical';

export class CerFormPaneSettings {
   visible?: CerFormPaneVisible = 'show';
   visibleWhenClose?: CerFormPaneVisible = 'collapse';
   size?: string;
   sizeSecondary?: string;
   splitter?: SplitterComponent;
   splitterIndex?: number;
   views?: CerFormPaneView[];
   dynamicOverride?: boolean = false;
}

export class CerFormPanesSettings {
   top?: CerFormPaneSettings;
   main?: CerFormPaneSettings;
   left?: CerFormPaneSettings;
   right?: CerFormPaneSettings;
   info?: CerFormPaneSettings;
   bottomLeft?: CerFormPaneSettings;
   bottom?: CerFormPaneSettings;
   bottomRight?: CerFormPaneSettings;
   orientationPrimary?: CerFormPanesOrientation = 'vertical';
}

export class CerFormPanesSettingsUtil {

   public static main(orientationPrimary: CerFormPanesOrientation = 'vertical', prepareSecondaryPanes: boolean = true): CerFormPanesSettings {
      const panes = new CerFormPanesSettings();
      panes.orientationPrimary = orientationPrimary;
      const master = new CerFormPaneSettings();
      master.visible = 'show';
      panes.main = master;
      if (prepareSecondaryPanes) {
         const rightSize = 40;
         const bottomSize = 30;
         panes.main.size = (100 - rightSize) + '%';
         panes.main.sizeSecondary = (100 - bottomSize) + '%';
         panes.right = new CerFormPaneSettings();
         panes.right.visible = 'collapse';
         panes.right.size = (100 - rightSize) + '%';
         panes.bottom = new CerFormPaneSettings();
         panes.bottom.visible = 'collapse';
         panes.bottom.size = (100 - bottomSize) + '%';
      }

      return panes;
   }
   
   public static masterDetail(orientationPrimary: CerFormPanesOrientation = 'vertical', detailVisible: boolean = true, detailSizePct: number = 50, detailSizeFixed: string = null): CerFormPanesSettings {
      // Master
      const master = new CerFormPaneSettings();
      master.size = (100 - detailSizePct) + '%';
      master.visible = 'show';

      // Detail
      const detail = new CerFormPaneSettings();
      detail.size = detailSizeFixed ?? detailSizePct + '%';
      detail.visible = detailVisible ? 'show' : 'collapse';

      // Panes
      const panes = new CerFormPanesSettings();
      panes.orientationPrimary = orientationPrimary;
      panes.main = master;
      if (orientationPrimary == 'vertical')
         panes.bottom = detail;
      else {
         panes.right = detail;
      }
      return panes;
   }

   public static panesAddOverflow(panes: CerFormPanesSettings) {
      const paneOverflow = new CerFormPaneSettings();
      paneOverflow.visible = 'collapse';
      paneOverflow.size = '50%';
      if (panes.main != null) {
         panes.main.size = '50%';
      }
      if (panes.orientationPrimary == 'horizontal' && panes.right == null) {
         panes.right = paneOverflow;
      }
      else if (panes.bottom == null) {
         {
            panes.bottom = paneOverflow;
         }
      }
   }

   public static paneEnsureVisible(pane: CerFormPaneSettings, makeVisible: boolean) : boolean {
      var changed = false;
      if (pane.visible == 'hide' || pane.visible == null) {
         pane.visible = makeVisible ? 'show' : 'collapse';
         changed = true;
      }
      if (pane.size == null) {
         pane.size = '50%';
         changed = true;
      }
      return changed;
   }

   public static paneDefaultGet(panes: CerFormPanesSettings): CerFormPaneType {
     return formPaneNames.find(paneName => CerFormPanesSettingsUtil.paneIsVisible(panes[paneName]) && panes[paneName]?.dynamicOverride === true);
   }

   public static paneVisibleCount(panes: CerFormPanesSettings): number {
      var c = 0;
      if (panes != null) {
         formPaneNames.forEach(paneName => { if (CerFormPanesSettingsUtil.paneIsVisible(panes[paneName])) { c++ } } );
      }
      return c;
   }

   public static paneIsHidden(pane: CerFormPaneSettings): boolean {
      return pane != null ? pane.visible == 'hide' : true;
   }

   public static paneIsVisible(pane: CerFormPaneSettings): boolean {
      return pane != null ? pane.visible != 'hide' : false;
   }
}

@Injectable()
export class CerFormPanesService implements OnDestroy {


   constructor(private formService: CerFormService) {
      this.manage(this.formService.formCommand$.subscribe(event => this.onCommand(event)));
      this.manage(this.formService.childCommand$.subscribe(event => this.onCommand(event)));
   }

   private subscriptionManager: Subscription = new Subscription();
   private manage(s: Subscription) {
      this.subscriptionManager.add(s);
   }

   ngOnDestroy() {
      this.subscriptionManager.unsubscribe();
   }

   private panes: CerFormPanesSettings;
   public panesChanged$: BehaviorSubject<CerFormPanesSettings> = new BehaviorSubject<CerFormPanesSettings>(null);

   public panesInit(panes: CerFormPanesSettings) {
      this.panes = panes;
      if (this.panes) {
         this.panesChanged$.next(this.panes);
      }
   }

   public panesGet(): CerFormPanesSettings {
      return this.panes;
   }

   public vSplitterLink(vSplitter: SplitterComponent, panes: CerFormPanesSettings) {
      //this.vSplitter = vSplitter;
      this.panes = panes;
      if (this.panes) {
         var idxNext: number = 0;
         idxNext = this.splitterLinkPane('top', vSplitter, idxNext);
         idxNext = this.splitterLinkPane('main', vSplitter, idxNext, panes.orientationPrimary == 'horizontal' || (this.panes.left == null && this.panes.right == null));
         idxNext = this.splitterLinkPane('bottom', vSplitter, idxNext);
         if (idxNext > 0) {
            this.panesChanged$.next(this.panes);
         }
      }
   }

   public hSplitterLink(hSplitter: SplitterComponent, panes: CerFormPanesSettings) {
      //this.hSplitter = hSplitter;
      this.panes = panes;
      if (this.panes) {
         var idxNext: number = 0;
         idxNext = this.splitterLinkPane('left', hSplitter, idxNext);
         idxNext = this.splitterLinkPane('main', hSplitter, idxNext, panes.orientationPrimary == 'vertical' || (this.panes.top == null && this.panes.bottom == null));
         idxNext = this.splitterLinkPane('right', hSplitter, idxNext);
         if (idxNext > 0) {
            this.panesChanged$.next(this.panes);
         }
      }
   }

   public hbSplitterLink(hbSplitter: SplitterComponent, panes: CerFormPanesSettings) {
      //this.hSplitter = hSplitter;
      this.panes = panes;
      if (this.panes) {
         var idxNext: number = 0;
         idxNext = this.splitterLinkPane('bottomLeft', hbSplitter, idxNext);
         idxNext = this.splitterLinkPane('bottom', hbSplitter, idxNext);
         idxNext = this.splitterLinkPane('bottomRight', hbSplitter, idxNext);
         if (idxNext > 0) {
            this.panesChanged$.next(this.panes);
         }
      }
   }

   private splitterLinkPane(pane: CerFormPane, splitter: SplitterComponent, idxNext: number, doMainLink: boolean = null): number {
      var s: CerFormPaneSettings = this.panes[pane];
      if (s != null && s.visible != 'hide') {
         if (doMainLink === true || pane != 'main') {
            this.panes[pane].splitter = splitter;
            this.panes[pane].splitterIndex = idxNext;
         }
         idxNext++;
      }
      return idxNext;
   }

   public onSplitterResizing(args: any) {
      this.formDesignChangeNext(UiActionTypeEnum.Splitter, args);
   }

   public onSplitterPaneState(args: any) {
      var splitter: SplitterComponent = this.splitterFromElement(args.element);
      var index: any = args.index;
      if (index) {
         if (Array.isArray(index) && index.length > 0) {
            index.forEach(i => this.visibleSetFromPaneObj(splitter, i));
         }
         else {
            this.visibleSetFromPaneObj(splitter, index);
         }
      }
      this.panesChanged$.next(this.panes);
      this.formDesignChangeNext(UiActionTypeEnum.Splitter, args);
   }

   private visibleSetFromPaneObj(splitter: SplitterComponent, index: number) {
      var pane: CerFormPane = this.paneFromSplitterIndex(splitter, index);
      if (pane) {
         var settings: CerFormPaneSettings = this.panes[pane];
         if (splitter?.paneSettings[index]) {
            var visible: CerFormPaneVisible = (splitter as any).allPanes[index].classList.contains('e-collapsed') ? 'collapse' : 'show';
            if (settings.visible != visible) {
               settings.visible = visible;
            }
         }
      }
   }

   private paneObjectSetState(pane: CerFormPane, expand: boolean, settings: CerFormPaneSettings = null): boolean {
      var changed: boolean = false;
      var settings: CerFormPaneSettings = this.panes[pane];
      if (settings != null) {
         var splitter = settings.splitter;
         var splitterIndex = settings.splitterIndex;
         if (splitter && splitterIndex >= 0) {
            if (expand) {
               if (splitter.paneSettings[splitterIndex].collapsed) {
                  changed = true;
                  if (this.splitterIsRendered(splitter)) {
                     splitter.expand(splitterIndex);
                  }
               }
            }
            else {
               if (!splitter.paneSettings[splitterIndex].collapsed) {
                  if (this.splitterIsRendered(splitter)) {
                     splitter.collapse(splitterIndex);
                  }
                  changed = true;
               }
            }
         }
         else {
            changed = true;
         }
      }
      return changed;
   }

   private splitterIsRendered(splitter: SplitterComponent): boolean {
      return (splitter?.element?.childNodes.length > 0);
   }

   private paneFromSplitterIndex(splitter: SplitterComponent, index: number): CerFormPane {
      var ret: CerFormPane = null;
      if (splitter != null && index >= 0) {
         formPaneNames.forEach(pane => {
            var settings: CerFormPaneSettings = this.panes[pane];
            if (settings?.splitter != null) {
               if (settings.splitter.orientation == splitter.orientation && settings.splitterIndex == index) {
                  ret = pane;
               }
            }
         });
      }
      return ret;
   }


   private splitterFromElement(element: any): SplitterComponent {
      var splitter: SplitterComponent = null;
      element?.ej2_instances?.forEach((obj: any) => {
         if (obj instanceof SplitterComponent) {
            splitter = obj as SplitterComponent;
         }
      });
      return splitter;
   }

   public onHSplitterExpanded(args: any) {
      this.formDesignChangeNext(UiActionTypeEnum.Splitter, args);
   }

   public onVSplitterExpanded(args: any) {
      this.formDesignChangeNext(UiActionTypeEnum.Splitter, args);
   }



   public formDesignChangeNext(action: UiActionTypeEnum, args: any) {
      this.formService.formDesignChangeNext(action, args);
      if (action == UiActionTypeEnum.PanesShow) {
         this.panesChanged$.next(this.panes);
      }
   }

   public onCommand(event: UiCommandEvent) {
      // Child events from grids etc. + form commands
      switch (event.commandId) {
         case uiCmdPaneRightToggle.id:
            this.toggle('right');
            break;
         case uiCmdPaneRightClose.id:
            this.close('right');
            break;
         case uiCmdPaneMainToggle.id:
            this.toggle('main');
            break;
         case uiCmdPaneMainClose.id:
            this.close('main');
            break;
         case uiCmdPaneBottomToggle.id:
            this.toggle('bottom');
            break;
         case uiCmdPaneBottomShow.id:
            this.show('bottom');
            break;
         case uiCmdPaneBottomClose.id:
            this.close('bottom');
            break;
         case uiCmdPaneBottomToggle.id:
            this.toggle('bottom');
            break;
         case uiCmdPaneBottomLeftToggle.id:
            this.toggle('bottomLeft');
            break;
         case uiCmdPaneBottomLeftShow.id:
            this.show('bottomLeft');
            break;
         case uiCmdPaneBottomLeftClose.id:
            this.close('bottomLeft');
            break;
         case uiCmdPaneBottomRightToggle.id:
            this.toggle('bottomRight');
            break;
         case uiCmdPaneBottomRightShow.id:
            this.show('bottomRight');
            break;
         case uiCmdPaneBottomRightClose.id:
            this.close('bottomRight');
            break;
         case uiCmdPaneRightReduce.id:
            this.reduce('right');
            event.cancel = true;
            break;
         case uiCmdPaneRightEnlarge.id:
            this.enlarge('right');
            event.cancel = true;
            break;
      }
   }


   // Handle visible panes
   public visibleSet(pane: CerFormPane, visibleTo: CerFormPaneVisible, settings: CerFormPaneSettings = null) {
      var settings = settings ?? this.panes[pane];
      if (settings) {
         var visibleClose = settings.visibleWhenClose ?? 'collapse'
         if (visibleClose == 'show') {
            if (visibleTo == 'collapse' || visibleTo == 'hide') {
               visibleTo = visibleClose;
            }
         }
         else if (visibleClose == 'collapse') {
            if (visibleTo == 'hide') {
               visibleTo = visibleClose;
            }
         }
         //if (settings.visible == 'disable') {
         //   visibleTo = 'disable';
         //}
         if (visibleTo != settings.visible) {
            var changed: boolean = this.visibleSetDo(pane, visibleTo, settings);
            if (changed) {
               this.formDesignChangeNext(UiActionTypeEnum.PanesShow, null);
            }
         }
      }
   }

   public visibleSetDo(pane: CerFormPane, visibleTo: CerFormPaneVisible, settings: CerFormPaneSettings = null): boolean {
      var changed: boolean = false;
      var settings = this.panes[pane];
      if (settings) {
         switch (visibleTo) {
            case 'show':
               changed = this.paneObjectSetState(pane, true, settings);
               break;
            case 'collapse':
               changed = this.paneObjectSetState(pane, false, settings);
               break;
            case 'hide':
               changed = this.paneObjectSetState(pane, false, settings);
               break;
         }
         settings.visible = visibleTo;
      }
      return changed;
   }

   public visible(pane: CerFormPane, settings: CerFormPaneSettings = null): boolean {
      var settings = settings ?? this.panes[pane];
      if (settings) {
         var visible: CerFormPaneVisible = settings.visible;
         return visible == null ? false : (visible == 'show' || visible == 'collapse');
      }
      return false;
   }

   public collapse(pane: CerFormPane, settings: CerFormPaneSettings = null) {
      var settings = settings ?? this.panes[pane];
      if (settings) {
         this.visibleSet(pane, 'collapse', settings);
      }
   }

   public close(pane: CerFormPane, settings: CerFormPaneSettings = null) {
      var settings = settings ?? this.panes[pane];
      if (settings) {
         var visibleTo = settings.visibleWhenClose ?? 'collapse';
         this.visibleSet(pane, visibleTo, settings);
      }
   }

   public show(pane: CerFormPane, settings: CerFormPaneSettings = null) {
      settings = settings ?? this.panes[pane];
      this.visibleSet(pane, 'show', settings);
   }

   public toggle(pane: CerFormPane) {
      if (this.panes) {
         var settings = this.panes[pane];
         if (settings) {
            switch (settings.visible) {
               case 'show':
                  this.close(pane, settings);
                  break;
               case 'hide':
               case 'collapse':
                  this.show(pane, settings);
                  break;
            }
         }
      }
   }
   private reduce(pane: CerFormPane) {
      this.resize(pane, -100, true);
   }

   private enlarge(pane: CerFormPane) {
      this.resize(pane, 100, true);
   }

   public resize(pane: CerFormPane, addPx: number, relative: boolean = false) {
      var settings = this.panes[pane];
      if (settings && settings.splitter && settings.splitterIndex >= 0) {
         var paneObj = settings.splitter.paneSettings[settings.splitterIndex];
         var newPx = relative ? Number(paneObj.size.substring(0, paneObj.size.length - 2)) + addPx : addPx;  // To-do use DOM element
         if (newPx <= 0) {
            this.close(pane);
         }
         if (newPx >= 0 && paneObj.collapsed) {
            this.show(pane);
         }
         paneObj.size = newPx + 'px';
         this.formService.formDesignChangeNext(UiActionTypeEnum.PanesShow);
      }
   }

   // Persistence
   private splitterIsResized: boolean = false; // Avoid persistence save unless splitter is resized
   public getPersistence(): any {
      var o: any = new Object();

      /*
      if (this.splitter?.paneSettings && this.splitter.paneSettings[1]) {
         o['collapsed'] = this.splitter.paneSettings[1].collapsed;
         var panesDimensions: any = (this.splitter as any).panesDimensions;
         if (this.splitterIsResized && panesDimensions && panesDimensions.length > 1) {
            o['width'] = panesDimensions[1] + "px";
         }
      }
      */
      //o = this.panes;
      return o;
   }

   public applyPersistence(o: any): boolean {
      if (o) {
         //this.panes = o;
         /*
         if this.hSplitter
         this.hSplitterLink()
            if (this.splitter?.paneSettings && this.splitter.paneSettings[1]) {
            this.splitter.paneSettings[1].collapsed = o['collapsed'];
            if (o['width']) {
               this.splitter.paneSettings[1].size = o['width'];
               this.detailVisibleRefresh(null, UiActionTypeEnum.PanesShow, true);
            }
         }
         */
      }
      return false;
   }
}
